// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { CircleNotch, UserPlus } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ProjectInviteDialog } from "../../components/ask-puntt/ProjectInviteDialog";
import { AskPunttArtifactProvider } from "../../components/ask-puntt-review";
import { AvatarWithInitials } from "../../components/AvatarWithInitials";
import {
  BREADCRUMB_HEADER_CHIP_CLASS,
  BREADCRUMB_TITLE_TEXT_CLASS,
  BreadcrumbHeaderRoot,
  BreadcrumbHeaderTrail,
} from "../../components/BreadcrumbHeaderRoot";
import { PageHeader } from "../../components/PageHeader";
import { buildMentionComposerMembers } from "../../utils/buildMentionComposerMembers";
import { useAppSelector } from "../../utils/hooks";
import {
  useAskPunttProject,
  useProjectReviews,
} from "../../utils/queries/askPunttProjects";
import { AskV2ConversationShell } from "../conversation/AskV2ConversationShell";
import { AskV2ProjectTabs } from "../conversation/AskV2ProjectTabs";
import { ASK_V2_PROJECTS_PATH } from "../paths";
import { AskV2RailToggle } from "../rail/AskV2RailToggle";
import { AskV2RightRail } from "../rail/AskV2RightRail";
import {
  collectRailContent,
  getReviewProgressMode,
  hasRailContent,
} from "../rail/collectRailContent";
import { mergeRailProgress } from "../rail/mergeRailProgress";
import { type AskV2RailSectionId } from "../rail/types";
import { useAskV2PlanProgress } from "../rail/useAskV2PlanProgress";
import { useAskV2RailReviewAutoOpen } from "../rail/useAskV2RailReviewAutoOpen";
import { AskV2ReviewWorkspace } from "../review/AskV2ReviewWorkspace";
import { useAskV2ReviewArtifactContext } from "../review/useAskV2ReviewArtifactContext";
import { useAskV2ReviewSidebarAutoCollapse } from "../review/useAskV2ReviewSidebarAutoCollapse";
import { useAskV2ReviewWorkspace } from "../review/useAskV2ReviewWorkspace";
import { AskV2ChatFileDrop } from "../transport/AskV2ChatFileDrop";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { AskV2ThreadLoading } from "../transport/ThreadLoading";
import { useAskV2Transport } from "../transport/useAskV2Transport";

type AskV2ProjectPageProps = {
  projectId: string;
  reviewId?: string;
  commentId?: string;
};

const DEFAULT_SECTIONS: Record<AskV2RailSectionId, boolean> = {
  progress: true,
  output: true,
  context: true,
};

export function AskV2ProjectPage({
  projectId,
  reviewId,
  commentId,
}: AskV2ProjectPageProps) {
  const { t } = useTranslation("ui");
  const [tab, setTab] = useState<"feed" | "chats">("feed");
  const [railOpen, setRailOpen] = useState(true);
  const [sectionOpen, setSectionOpen] = useState(DEFAULT_SECTIONS);
  const [inviteOpen, setInviteOpen] = useState(false);
  const workspace = useAskV2ReviewWorkspace({
    scope: { kind: "project", projectId },
    reviewId,
    commentId,
  });
  const projectQuery = useAskPunttProject(projectId);
  const reviewsQuery = useProjectReviews(projectId);
  const project = projectQuery.data?.project;
  const members = project?.members ?? [];
  const sharedChatId = project?.sharedChatId ?? null;
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const viewer = members.find((member) => member._id === currentUserId);
  const bindFeedTransport = tab === "feed" || workspace.isReviewOpen;
  const transport = useAskV2Transport({
    chatId: bindFeedTransport ? sharedChatId : null,
    enabled: bindFeedTransport && !!sharedChatId,
    presentation: "feed",
  });
  const owner = members.find((member) => member._id === project?.ownerId);
  const ownerName = owner?.name ?? owner?.email ?? "Owner";
  const isSharedChatOwner =
    !!currentUserId && transport.historyChat?.userId === currentUserId;
  const authorNameByUserId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const member of members) {
      const label = member.name?.trim() || member.email?.trim();
      if (member._id && label) map[member._id] = label;
    }
    return map;
  }, [members]);
  const mentionComposerMembers = useMemo(
    () =>
      buildMentionComposerMembers(
        isSharedChatOwner ? members : [],
        transport.historyChat?.allowList ?? [],
      ),
    [isSharedChatOwner, members, transport.historyChat?.allowList],
  );
  // why: omitted canInteract fails open; only an explicit false is a read-only shared feed that must ignore drops
  const fileDropDisabled = transport.historyChat?.canInteract === false;
  const feedSummonBar = (
    <AskV2SummonBar
      transport={transport}
      requireBoundChatId
      mentionMembers={mentionComposerMembers}
      rosterMembers={isSharedChatOwner ? members : []}
      enableDirectoryMentions={isSharedChatOwner}
      composerAriaLabel={t("replyToProjectFeed")}
    />
  );
  const progressMode = getReviewProgressMode({
    chat: transport.historyChat,
    messages: transport.historyMessages,
    reviews: reviewsQuery.data,
  });
  const planProgress = useAskV2PlanProgress({
    messages: transport.historyMessages,
    mode: progressMode,
  });
  const setRailOpenStable = useCallback(
    (open: boolean) => setRailOpen(open),
    [],
  );
  const setSectionOpenStable = useCallback(
    (
      updater: (
        current: Record<AskV2RailSectionId, boolean>,
      ) => Record<AskV2RailSectionId, boolean>,
    ) => setSectionOpen(updater),
    [],
  );
  useAskV2RailReviewAutoOpen({
    mode: progressMode,
    setRailOpen: setRailOpenStable,
    setSectionOpen: setSectionOpenStable,
  });
  const railContent = useMemo(() => {
    const base = collectRailContent({
      project,
      chat: transport.historyChat,
      messages: transport.historyMessages,
      reviews: reviewsQuery.data,
    });
    return mergeRailProgress(base, planProgress);
  }, [
    project,
    reviewsQuery.data,
    planProgress,
    transport.historyChat,
    transport.historyMessages,
  ]);
  const railHasContent = hasRailContent(railContent);
  const showRailToggle = !workspace.isReviewOpen && railHasContent;
  const versionOptions = useMemo(
    () =>
      (reviewsQuery.data?.reviews ?? [])
        .filter((item) => item.status !== "in_review")
        .map((item) => ({
          reviewId: item.id,
          title: item.title ?? item.name,
        })),
    [reviewsQuery.data?.reviews],
  );
  const artifactContext = useAskV2ReviewArtifactContext({
    workspace,
    messages: transport.historyMessages,
  });
  useAskV2ReviewSidebarAutoCollapse(workspace.isReviewOpen);

  if (projectQuery.isLoading) {
    return (
      <div
        className="ask-puntt-surface flex h-full items-center justify-center"
        data-testid="ask-v2-project-loading"
      >
        <CircleNotch
          size={28}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (projectQuery.isError || !project) {
    return (
      <div
        className="ask-puntt-surface flex h-full flex-col items-center justify-center gap-4"
        data-testid="ask-v2-project-error"
      >
        <Text size="4" weight="medium" className="text-puntt-neutral-gray-11">
          {t("campaign.couldNotLoadProject")}
        </Text>
        <a
          href={ASK_V2_PROJECTS_PATH}
          className="text-sm text-puntt-accent-9 hover:underline"
          data-testid="ask-v2-project-error-back"
        >
          {t("campaign.backToAllProjects")}
        </a>
      </div>
    );
  }

  return (
    <AskV2ChatFileDrop disabled={fileDropDisabled}>
      <AskV2ConversationShell
        testId="ask-v2-project-page"
        fullBleed={workspace.isReviewOpen}
        scrollResetKey={sharedChatId}
        scrollDeps={
          workspace.isReviewOpen || tab !== "feed" || !sharedChatId
            ? undefined
            : [transport.displayMessages]
        }
        autoScrollEnabled={!transport.inFlight}
        scrollSentSignal={transport.sending}
        onAddQuotedContext={
          workspace.isReviewOpen ? undefined : transport.addQuotedContext
        }
        header={
          <PageHeader className="w-full min-w-0 max-w-full gap-3 overflow-hidden border-puntt-neutral-gray-5">
            <BreadcrumbHeaderTrail className="min-w-0 flex-1 gap-6 overflow-hidden">
              <BreadcrumbHeaderRoot
                to={ASK_V2_PROJECTS_PATH}
                label={t("allProjects")}
                testId="ask-v2-project-breadcrumb-all"
              />
              <span
                className={`${BREADCRUMB_TITLE_TEXT_CLASS} truncate`}
                data-testid="ask-v2-project-title"
              >
                {project.name}
              </span>
              <span
                className={`hidden shrink-0 rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[11px] font-medium text-puntt-neutral-gray-11 sm:inline ${BREADCRUMB_HEADER_CHIP_CLASS}`}
                data-testid="ask-v2-project-owner"
              >
                {t("ownedBy")} {ownerName}
              </span>
              <span className="sr-only" data-testid="ask-v2-project-id">
                {projectId}
              </span>
              {!workspace.isReviewOpen ? (
                <AskV2ProjectTabs
                  variant="header"
                  data-testid="ask-v2-project-tabs"
                  active={tab}
                  feedLabel={t("sharedProjectFeed")}
                  chatsLabel={t("privateChats")}
                  viewsLabel={t("projectViews")}
                  onChange={setTab}
                />
              ) : null}
            </BreadcrumbHeaderTrail>
            <div className="ml-auto flex shrink-0 items-center gap-3">
              {members.length > 0 ? (
                <div
                  className="flex shrink-0 items-center -space-x-2"
                  data-testid="ask-v2-project-members"
                >
                  {members.slice(0, 5).map((member) => (
                    <AvatarWithInitials
                      key={member._id}
                      size={7}
                      name={member.name ?? member.email ?? "Member"}
                      avatar={member.avatar ?? undefined}
                      colorKey={member._id}
                      className="ring-2 ring-puntt-neutral-gray-1"
                    />
                  ))}
                  {members.length > 5 ? (
                    <span
                      className="flex size-7 items-center justify-center rounded-full bg-puntt-neutral-gray-3 text-[10px] font-medium text-puntt-neutral-gray-11 ring-2 ring-puntt-neutral-gray-1"
                      data-testid="ask-v2-project-members-overflow"
                    >
                      +{members.length - 5}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                aria-label={t("inviteToProject")}
                data-testid="ask-v2-project-invite"
                className="flex shrink-0 items-center gap-1.5 rounded-md border border-puntt-neutral-gray-7 bg-base-white px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12 transition-colors hover:border-puntt-accent-9 hover:bg-puntt-blue-2 hover:text-puntt-accent-9"
              >
                <UserPlus size={15} />
                {t("invite")}
              </button>
              {showRailToggle ? (
                <AskV2RailToggle
                  open={railOpen}
                  onToggle={() => setRailOpen((current) => !current)}
                />
              ) : null}
            </div>
          </PageHeader>
        }
        composer={workspace.isReviewOpen ? undefined : feedSummonBar}
        rail={
          showRailToggle ? (
            <AskV2RightRail
              open={railOpen}
              content={railContent}
              sectionOpen={sectionOpen}
              onReviewOpen={workspace.openReview}
              onSectionOpenChange={(id, open) =>
                setSectionOpen((current) => ({ ...current, [id]: open }))
              }
            />
          ) : null
        }
      >
        {workspace.isReviewOpen ? (
          <AskV2ReviewWorkspace
            workspace={workspace}
            transport={transport}
            versionOptions={versionOptions}
            feedPresentation="feed"
            feedActorName={viewer?.name ?? viewer?.email ?? undefined}
            feedActorAvatar={viewer?.avatar ?? undefined}
            feedMembers={mentionComposerMembers}
            currentUserId={currentUserId}
            authorNameByUserId={authorNameByUserId}
            artifactContext={artifactContext}
            composer={feedSummonBar}
          />
        ) : (
          <AskPunttArtifactProvider value={artifactContext}>
            {tab === "chats" ? (
              <p
                className="py-8 text-center text-[11px] font-medium uppercase tracking-[0.08em] text-puntt-neutral-gray-9"
                data-testid="ask-v2-project-chats-empty"
              >
                {t("campaignNewChatDraft.emptyLabel")}
              </p>
            ) : !sharedChatId ? (
              <p
                className="py-8 text-center text-sm text-puntt-neutral-gray-10"
                data-testid="ask-v2-project-feed-empty"
              >
                {t("campaignNewChatDraft.emptyLabel")}
              </p>
            ) : transport.historyLoading && transport.messages.length === 0 ? (
              <AskV2ThreadLoading />
            ) : transport.historyError && transport.messages.length === 0 ? (
              <Text
                as="p"
                size="2"
                color="gray"
                data-testid="ask-v2-thread-error"
                className="py-8 text-center"
              >
                {t("askV2.pages.chat.loadError")}
              </Text>
            ) : (
              <AskV2Thread
                transport={transport}
                presentation="feed"
                feedActorName={viewer?.name ?? viewer?.email ?? undefined}
                feedActorAvatar={viewer?.avatar ?? undefined}
                feedMembers={mentionComposerMembers}
                currentUserId={currentUserId}
              />
            )}
          </AskPunttArtifactProvider>
        )}
      </AskV2ConversationShell>
      <ProjectInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        projectId={projectId}
        projectName={project.name}
        members={members}
      />
    </AskV2ChatFileDrop>
  );
}
